import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import { alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { rsvpApi } from './api';
import { RsvpHeader } from './parts/RsvpHeader';
import { SessionAccordion } from './parts/SessionAccordion';
import { AttendanceAccordion } from './parts/AttendanceAccordion';
import { RightsAccordion } from './parts/RightsAccordion';
import { RulesAccordion } from './parts/RulesAccordion';
import { FooterBar } from './parts/FooterBar';

function resolveLoadError(err) {
  if (err?.status === 410) {
    return 'Link suresi dolmustur. Varsayilan olarak geleceginiz kabul edilir.';
  }
  if (err?.status === 404) {
    return 'Bu link gecersiz veya artik kullanilamiyor.';
  }
  return err?.message || 'Sayfa yuklenemedi. Link gecersiz veya suresi dolmus olabilir.';
}

export default function RsvpPage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  useEffect(() => {
    let alive = true;

    async function run() {
      setLoading(true);
      setError('');

      try {
        const res = await rsvpApi.get(token);
        if (!alive) return;
        setData(res);
      } catch (err) {
        if (!alive) return;
        setData(null);
        setError(resolveLoadError(err));
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [token]);

  const attStatus = data?.attendance?.status || 'UNKNOWN';
  const remainingChanges = Number.isFinite(data?.attendance?.remainingChanges)
    ? data.attendance.remainingChanges
    : 0;
  const canAnswer = !!data && remainingChanges > 0 && !saving;

  async function onAnswer(answer) {
    setSaving(true);
    setError('');

    try {
      const res = await rsvpApi.answer(token, answer);
      setData(res);
      setSnack({
        open: true,
        msg: answer === 'YES' ? 'Katilim onayin kaydedildi.' : 'Katilamayacagin bilgisi alindi.',
        severity: 'success',
      });
    } catch (err) {
      const message = resolveLoadError(err);
      setError(message);
      setSnack({
        open: true,
        msg: err?.status === 410 ? 'Link artik aktif degil.' : 'Islem basarisiz.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <MDBox
      minHeight="100vh"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        background:
          'radial-gradient(960px 520px at 8% 0%, rgba(14,165,233,0.14) 0%, transparent 60%), linear-gradient(180deg, #f8fbff 0%, #eef4f7 100%)',
      }}
    >
      <RsvpHeader
        companyName={data?.company?.name}
        tokenMasked={data?.tokenMasked}
        attStatus={attStatus}
      />

      <MDBox
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: 760,
          mx: 'auto',
          px: { xs: 1.1, sm: 1.5 },
          py: { xs: 1, sm: 1.4 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <MDBox
            sx={{
              flex: 1,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <MDBox
              sx={{
                p: 2.2,
                borderRadius: '24px',
                bgcolor: '#fff',
                border: '1px solid rgba(15,23,42,0.08)',
                boxShadow: '0 18px 50px rgba(15,23,42,0.08)',
                display: 'grid',
                justifyItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress />
              <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
                Sayfa hazirlaniyor...
              </MDTypography>
            </MDBox>
          </MDBox>
        ) : !data ? (
          <MDBox
            sx={{
              maxWidth: 620,
              mx: 'auto',
              mt: 3,
              p: 2,
              borderRadius: '24px',
              bgcolor: '#fff',
              border: '1px solid rgba(15,23,42,0.08)',
              boxShadow: '0 18px 50px rgba(15,23,42,0.08)',
            }}
          >
            <Alert severity="warning" sx={{ borderRadius: '16px' }}>
              {error || 'Bu link artik kullanilamiyor.'}
            </Alert>

            <MDBox
              sx={{
                mt: 1.4,
                p: 1.2,
                borderRadius: '16px',
                background: alpha('#0f172a', 0.03),
              }}
            >
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Link suresi dolduysa sistem bu seansi varsayilan olarak gelecek sekilde yorumlar.
                Degisiklik icin studyo ile iletisime gecebilirsin.
              </MDTypography>
            </MDBox>
          </MDBox>
        ) : (
          <MDBox display="grid" gap={0.8}>
            {error ? (
              <Alert severity="warning" sx={{ borderRadius: '16px' }}>
                {error}
              </Alert>
            ) : null}

            <Alert
              severity="info"
              sx={{
                borderRadius: '16px',
                py: 0.5,
                '& .MuiAlert-message': { py: 0, fontSize: 12.5, lineHeight: 1.35 },
              }}
            >
              Saat 23:00 sonrasinda bu link kapanir. Isaretleme yapilmazsa varsayilan olarak
              gelecegin kabul edilir.
            </Alert>

            <SessionAccordion
              memberName={data?.member?.fullName}
              trainerName={data?.trainer?.fullName}
              serviceName={data?.session?.serviceName}
              studioName={data?.session?.studioName}
              startAt={data?.session?.startAt}
              endAt={data?.session?.endAt}
            />

            <AttendanceAccordion
              remainingChanges={data?.attendance?.remainingChanges}
              saving={saving}
              canAnswer={canAnswer}
              onAnswer={onAnswer}
              paymentReminder={data?.paymentReminder}
              attendanceInsights={data?.attendanceInsights}
              currentStatus={data?.attendance?.status}
            />

            {data?.inquiryConfig?.showRemainingRights ? (
              <RightsAccordion
                remainingRights={data?.remainingRights}
                lastSessions={data?.lastSessions}
              />
            ) : null}

            {data?.inquiryConfig?.showBusinessRules ? (
              <RulesAccordion companyRules={data?.companyRules} />
            ) : null}

            <FooterBar />
          </MDBox>
        )}
      </MDBox>

      <Snackbar
        open={snack.open}
        autoHideDuration={2600}
        onClose={() => setSnack((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((current) => ({ ...current, open: false }))}
          severity={snack.severity}
          sx={{ borderRadius: '12px' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}
